import React, { Fragment } from 'react'
import styled from '@emotion/styled'
import Header from 'library/molecules/Header'
import Layout from 'library/atoms/Layout'
import Link from 'next/link'
import Item, { ItemTextCta, ItemUl, ItemText } from 'library/molecules/Item'
import CoverImage from 'library/atoms/CoverImage'
import HeadContainer from 'library/atoms/HeadContainer'
import useTranslation from 'next-translate/useTranslation'
import translateChargeableTitle from 'helpers/translateChargeableTitle'
import { TopLevelCard } from 'helpers/determineTopLevelChargeableData'
import { OutletPresenter, SchedulablePresenter } from 'shared/presenters'
import Icon from 'shared/library/atoms/Icon'
import { H5, H3 } from 'library/atoms/Typography'
import { ShowOnMarketplace } from 'shared/library/atoms/ConditionalMarketplace'
import { Text } from 'library/atoms/Typography'
import useStoreMarketplaceReferrals from 'state/useStoreMarketplaceReferrals'
import GoogleStructuredDataBreadcrumb from 'library/molecules/GoogleStructuredData/GoogleStructuredDataBreadcrumbs'
import widgetRoutes from 'enums/routes'
import { useRouter } from 'next/router'
import {
  arrayToString,
  stripHTMLTags,
  truncateString
} from 'shared/helpers/stringUtilities'
import { RecursivePartial, definedNotNull } from 'shared/presenters/presenter'
import { Active_Schedulables } from 'shared/presenters/graphqlTypes'

interface HomeCard extends Omit<TopLevelCard, 'schedulable'> {
  schedulable: SchedulablePresenter
}

interface HomeProps {
  cards: HomeCard[]
  outlet: OutletPresenter
}

const Home = ({ cards, outlet }: HomeProps) => {
  const { t } = useTranslation('common')
  const { query } = useRouter()
  const widgetSlug = query.widgetSlug as string

  const metaDescription = outlet.description()
    ? truncateString(stripHTMLTags(outlet.description()), 200)
    : t(
        outlet?.address?.city
          ? 'meta-description-with-location'
          : 'meta-description-without-location',
        {
          outlet: outlet.name,
          location: outlet?.address?.city,
          activities: outlet.active_schedulables
            ? arrayToString(
                outlet?.active_schedulables.map(
                  (s: RecursivePartial<Active_Schedulables>) =>
                    definedNotNull(definedNotNull(s.activity_type).name)
                )
              )
            : t('common:activities').toLowerCase()
        }
      )

  // Store marketplace source for session if utm set
  useStoreMarketplaceReferrals()

  return (
    <>
      <HeadContainer
        title={`${outlet.name} - ${t('book-now')}`}
        description={metaDescription}
      >
        {!outlet.feature_toggles?.marketplace_enabled ||
          (outlet.admin_forced_marketplace_disable && (
            <meta name="robots" content="noindex" />
          ))}
      </HeadContainer>
      <StyledLayout>
        <Header />
        <main>
          <ShowOnMarketplace>
            <H3 textAlign="center">{outlet.name}</H3>
            <br />
            {/* <Text
              fontStyle="p2"
              color="secondary"
              dangerouslySetInnerHTML={{ __html: outlet.description() }}
            /> */}
          </ShowOnMarketplace>
          {cards.length <= 3 ? (
            <ItemUl as="div">
              {cards?.map(card => {
                return (
                  <Fragment key={card.title}>
                    <StyledLink href={card.href}>
                      <Item
                        variant="card"
                        as="li"
                        title={translateChargeableTitle(card.title, t)}
                      >
                        <StyledCoverImage src={card.cardImage} />
                        <ItemTextCta>
                          <H5>{translateChargeableTitle(card.title, t)}</H5>
                          <Icon name="arrow-right" size={24} />
                        </ItemTextCta>
                      </Item>
                    </StyledLink>
                  </Fragment>
                )
              })}
            </ItemUl>
          ) : (
            <StyledItemUl as="div" noGap>
              {cards?.map(card => {
                return (
                  <Fragment key={card.title}>
                    <StyledLink href={card.href}>
                      <Item
                        variant="slimCard"
                        title={translateChargeableTitle(card.title, t)}
                      >
                        <CoverImage src={card.cardImage} variant="thumb" />
                        <ItemTextCta>
                          <H5>{translateChargeableTitle(card.title, t)}</H5>
                        </ItemTextCta>
                        <Icon name="card-arrow" size={24} />
                      </Item>
                    </StyledLink>
                  </Fragment>
                )
              })}
            </StyledItemUl>
          )}
          <ShowOnMarketplace>
            <MarketplaceItemUl>
              {outlet.description() && (
                <Item>
                  <ItemText>
                    <H5>{t('common:about-outlet', { name: outlet.name })}</H5>
                    <Text
                      as="div"
                      color="secondary"
                      dangerouslySetInnerHTML={{
                        __html: outlet.description()
                      }}
                    />
                  </ItemText>
                </Item>
              )}
              <Item>
                <ItemText>
                  <H5>{t('common:about-outlet', { name: 'eola' })}</H5>
                  <Text color="secondary">{t('common:about-eola-body')}</Text>
                </ItemText>
              </Item>
            </MarketplaceItemUl>
          </ShowOnMarketplace>
          <GoogleStructuredDataBreadcrumb
            breadcrumbs={[
              {
                link: widgetRoutes.HOME.url({ widgetSlug: widgetSlug }),
                name: definedNotNull(outlet.name),
                image: outlet.logoImageURL()
              }
            ]}
          />
        </main>
      </StyledLayout>
    </>
  )
}

export default Home

const StyledLayout = styled(Layout)(({ theme }) => ({
  background: theme.colors.white
}))

const StyledItemUl = styled(ItemUl)(({ theme }) => ({
  a: {
    borderBottom: `1px solid ${theme.colors.border.primary}`
  },
  'a:last-child': {
    borderBottom: 'none'
  }
}))

const MarketplaceItemUl = styled(ItemUl)(({ theme }) => ({
  marginTop: theme.space[4]
}))

const StyledLink = styled(Link)(() => ({
  textDecoration: 'none',
  display: 'block',
  width: '100%'
}))

const StyledCoverImage = styled(CoverImage)(({ theme }) => ({
  marginBottom: theme.space[4]
}))
